import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

// material-ui
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

// project import
import NavItem from './NavItem';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// assets
import DownOutlined from '@ant-design/icons/DownOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';

export default function NavCollapse({ item, level = 1 }) {
  const { pathname } = useLocation();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const isActive = useMemo(
    () => item.children?.some((child) => child.url && matchPath({ path: child.url, end: false }, pathname)),
    [item.children, pathname]
  );
  const [open, setOpen] = useState(isActive);

  useEffect(() => {
    if (isActive) {
      setOpen(true);
    }
  }, [isActive]);

  const handleClick = () => {
    if (!drawerOpen) {
      setOpen(true);
      handlerDrawerOpen(true); // 收起态点击先展开抽屉，同时展开子菜单
    } else {
      setOpen((current) => !current);
    }
  };

  const Icon = item.icon;
  const ArrowIcon = open ? DownOutlined : RightOutlined;
  const textColor = isActive ? 'primary.main' : 'text.primary';

  return (
    <Box>
      <ListItemButton
        selected={isActive}
        onClick={handleClick}
        sx={{
          zIndex: 1201,
          pl: drawerOpen ? `${level * 28}px` : 1.5,
          py: !drawerOpen && level === 1 ? 1.25 : 1,
          ...(drawerOpen && {
            '&:hover': { bgcolor: 'primary.lighter' },
            '&.Mui-selected': {
              bgcolor: 'primary.lighter',
              color: 'primary.main',
              '&:hover': { color: 'primary.main', bgcolor: 'primary.lighter' }
            }
          }),
          ...(!drawerOpen && {
            '&:hover': { bgcolor: 'transparent' },
            '&.Mui-selected': { bgcolor: 'transparent', '&:hover': { bgcolor: 'transparent' } }
          })
        }}
      >
        {Icon && (
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: textColor,
              ...(!drawerOpen && {
                borderRadius: 1.5,
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isActive ? 'primary.lighter' : 'transparent',
                '&:hover': { bgcolor: isActive ? 'primary.lighter' : 'secondary.lighter' }
              })
            }}
          >
            <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} />
          </ListItemIcon>
        )}
        {drawerOpen && (
          <>
            <ListItemText
              primary={
                <Typography variant="h6" sx={{ color: textColor }}>
                  {item.title}
                </Typography>
              }
            />
            <ArrowIcon style={{ fontSize: '0.75rem' }} />
          </>
        )}
      </ListItemButton>

      <Collapse in={drawerOpen && open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {item.children?.map((menuItem) => {
            if (menuItem.type === 'item') {
              return <NavItem key={menuItem.id} item={menuItem} level={level + 1} />;
            }

            if (menuItem.type === 'collapse') {
              return <NavCollapse key={menuItem.id} item={menuItem} level={level + 1} />;
            }

            return (
              <Typography key={menuItem.id} variant="h6" sx={{ color: 'error.main', textAlign: 'center' }}>
                Fix - Collapse Items
              </Typography>
            );
          })}
        </List>
      </Collapse>
    </Box>
  );
}

NavCollapse.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};
